'use client';
import React, { useState, useEffect } from 'react';
import { WeatherData } from '@/lib/types';
import { calculateDensityAltitude } from '@/lib/densityAltitudeCalculator';
import {
  CYRC_RUNWAY_OPTIONS,
  getRecommendedRunway,
  calculateRunwayWindComponents,
} from '@/lib/takeoffPerformanceCalculator';

interface Props {
  weatherData: WeatherData;
}

// ── Vigilance levels ──────────────────────────────────────────────────────────
type VigilanceLevel = 'favorable' | 'valider' | 'prudence' | 'risque';

interface LevelInfo {
  level: VigilanceLevel;
  label: string;
  shortMsg: string;
  color: string;
  bg: string;
  border: string;
  gaugeIndex: number; // 0-3
}

const LEVELS: Record<VigilanceLevel, LevelInfo> = {
  favorable: {
    level: 'favorable',
    label: 'Favorable',
    shortMsg: 'Conditions favorables',
    color: '#16a34a',
    bg: '#f0fdf4',
    border: '#86efac',
    gaugeIndex: 0,
  },
  valider: {
    level: 'valider',
    label: 'À valider',
    shortMsg: 'Vérifier les performances',
    color: '#d97706',
    bg: '#fffbeb',
    border: '#fde68a',
    gaugeIndex: 1,
  },
  prudence: {
    level: 'prudence',
    label: 'Prudence',
    shortMsg: 'Prudence — conditions dégradées',
    color: '#ea580c',
    bg: '#fff7ed',
    border: '#fdba74',
    gaugeIndex: 2,
  },
  risque: {
    level: 'risque',
    label: 'Risque accru',
    shortMsg: 'Risque accru — calcul performance fortement recommandé',
    color: '#dc2626',
    bg: '#fef2f2',
    border: '#fca5a5',
    gaugeIndex: 3,
  },
};

const PILOT_MESSAGES: Record<VigilanceLevel, string> = {
  favorable:
    "Conditions de performance généralement favorables. Vérifier tout de même le manuel de vol, la masse et centrage et les conditions de piste.",
  valider:
    "Certaines conditions peuvent réduire les performances. Valider la distance de décollage et le taux de montée selon votre aéronef.",
  prudence:
    "Conditions pouvant rallonger le décollage ou réduire le taux de montée. Calcul performance recommandé avant départ.",
  risque:
    "Conditions défavorables possibles. Calcul performance complet requis selon le POH, masse et centrage, obstacles et piste disponible.",
};

// ── Level scoring helpers ─────────────────────────────────────────────────────
function scoreDA(ft: number): number {
  if (ft < 1500) return 0;
  if (ft < 2500) return 1;
  if (ft < 3500) return 2;
  return 3;
}

function scoreTemp(c: number): number {
  if (c < 20) return 0;
  if (c < 25) return 1;
  if (c < 30) return 2;
  return 3;
}

function scoreHumidity(pct: number): number {
  if (pct < 60) return 0;
  if (pct < 75) return 1;
  return 2;
}

function scoreWind(headwindKt: number, tailwindKt: number, crosswindKt: number): number {
  let s = 0;
  if (headwindKt > 0) s = Math.max(s, 0);
  if (tailwindKt > 5) s = Math.max(s, 3);
  else if (tailwindKt > 3) s = Math.max(s, 2);
  if (crosswindKt > 15) s = Math.max(s, 3);
  else if (crosswindKt > 10) s = Math.max(s, 2);
  return s;
}

function indexToLevel(i: number): VigilanceLevel {
  if (i === 0) return 'favorable';
  if (i === 1) return 'valider';
  if (i === 2) return 'prudence';
  return 'risque';
}

// ── Factor badge ──────────────────────────────────────────────────────────────
interface FactorBadgeProps {
  score: number;
  label: string;
}
function FactorBadge({ score, label }: FactorBadgeProps) {
  const info = LEVELS[indexToLevel(score)];
  return (
    <span
      className="text-xs font-bold px-2 py-0.5 rounded-full"
      style={{ background: info.color, color: '#fff' }}
    >
      {label}
    </span>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function PerformanceContent({ weatherData }: Props) {
  const { metar, supplemental } = weatherData;
  const [manualRunway, setManualRunway] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);

  // ── Calculations ──
  const da = calculateDensityAltitude(metar.temperature, metar.pressure);

  const autoRunway = getRecommendedRunway(metar.windDirection, metar.windSpeed);
  const selectedRunwayId = manualRunway ?? autoRunway.id;
  const selectedRunway = CYRC_RUNWAY_OPTIONS.find((r) => r.id === selectedRunwayId) ?? autoRunway;

  const wind = calculateRunwayWindComponents(
    metar.windDirection,
    metar.windSpeed,
    selectedRunway.heading
  );

  // ── Vigilance scoring ──
  const daScore = scoreDA(da.densityAltitudeFt);
  const tempScore = scoreTemp(metar.temperature);
  const humScore = scoreHumidity(supplemental.humidity);
  const windScore = scoreWind(wind.headwindKt, wind.tailwindKt, wind.crosswindKt);
  const globalScore = Math.max(daScore, tempScore, humScore, windScore);
  const globalLevel = indexToLevel(globalScore);
  const levelInfo = LEVELS[globalLevel];

  // ── Factor labels ──
  const daLabel = daScore === 0 ? 'Favorable' : daScore === 1 ? 'À valider' : daScore === 2 ? 'Prudence' : 'Risque accru';
  const tempLabel = tempScore === 0 ? 'Favorable' : tempScore === 1 ? 'À valider' : tempScore === 2 ? 'Prudence' : 'Risque accru';
  const humLabel = humScore === 0 ? 'Favorable' : humScore === 1 ? 'À valider' : 'Prudence';
  const windLabel = windScore === 0 ? 'Favorable' : windScore === 1 ? 'Neutre' : windScore === 2 ? 'Prudence' : 'Risque accru';

  // ── Wind description ──
  const windDesc =
    wind.headwindKt > 0
      ? `Face ${wind.headwindKt} kt`
      : wind.tailwindKt > 0
      ? `Arrière ${wind.tailwindKt} kt`
      : `Variable ${metar.windSpeed} kt`;

  // ── Visibility / ceiling ──
  const visText = metar.visibility != null ? `${metar.visibility} SM` : '—';
  const ceilingText = metar.ceiling != null ? `${metar.ceiling} ft` : '—';
  const visScore = 0; // informational only

  if (!mounted) return null;

  return (
    <div
      className="flex flex-col bg-slate-50"
      style={{ overflowY: 'auto', overflowX: 'hidden', height: '100%', paddingBottom: 'calc(env(safe-area-inset-bottom) + 4px)' }}
    >

      {/* ── TITLE ── */}
      <div className="shrink-0 px-3 pt-2 pb-1 flex items-center gap-2">
        <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
          <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/>
        </svg>
        <h1 className="text-sm font-black text-slate-800 tracking-wide">Vigilance performance</h1>
        <span className="ml-auto text-xs font-semibold text-slate-400">{weatherData.dataSource} · {metar.time}</span>
      </div>

      {/* ── CONTENT ── */}
      <div className="flex flex-col gap-1.5 px-2 pb-2">

        {/* ── CARD 1: Vigilance globale (full width) ── */}
        <div
          className="rounded-xl border-2 p-3 flex flex-col gap-2"
          style={{ background: levelInfo.bg, borderColor: levelInfo.border }}
        >
          {/* Header row */}
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
              style={{ background: levelInfo.color + '22' }}
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" style={{ color: levelInfo.color }}>
                <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/>
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wide leading-none" style={{ fontSize: '0.6rem' }}>VIGILANCE PERFORMANCE</div>
              <div className="text-2xl font-black leading-tight" style={{ color: levelInfo.color }}>{levelInfo.label}</div>
              <div className="text-xs font-semibold text-slate-600 leading-tight mt-0.5">{levelInfo.shortMsg}</div>
            </div>
            {/* DA badge */}
            <div className="shrink-0 text-right">
              <div className="text-xs font-bold text-slate-500 uppercase leading-none" style={{ fontSize: '0.55rem' }}>DENSITÉ-ALT.</div>
              <div className="text-xl font-black font-tabular leading-tight" style={{ color: da.levelColor }}>{da.densityAltitudeFt} ft</div>
            </div>
          </div>

          {/* Horizontal gauge */}
          <div>
            <div className="flex rounded-lg overflow-hidden border border-slate-200 h-7 relative">
              {(['favorable', 'valider', 'prudence', 'risque'] as VigilanceLevel[]).map((lv) => {
                const li = LEVELS[lv];
                const isActive = lv === globalLevel;
                return (
                  <div
                    key={lv}
                    className="flex-1 flex items-center justify-center relative transition-all"
                    style={{ background: isActive ? li.color : li.color + '30' }}
                  >
                    <span className="text-xs font-bold leading-none" style={{ color: isActive ? '#fff' : li.color, fontSize: '0.6rem' }}>
                      {li.label}
                    </span>
                    {isActive && (
                      <div
                        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0"
                        style={{ borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderBottom: `5px solid ${li.color}`, bottom: '-5px' }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
            <div className="flex mt-1">
              {(['favorable', 'valider', 'prudence', 'risque'] as VigilanceLevel[]).map((lv) => {
                const li = LEVELS[lv];
                return (
                  <div key={lv} className="flex-1 flex justify-center">
                    <div className="w-2 h-2 rounded-full" style={{ background: lv === globalLevel ? li.color : li.color + '40' }} />
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── CARDS 2+3: Facteurs + Piste — 2-col on desktop, 1-col on mobile ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">

          {/* ── CARD 2: Facteurs du jour ── */}
          <div className="rounded-xl border-2 border-blue-200 bg-white p-3 flex flex-col gap-2" style={{ boxShadow: '0 2px 8px rgba(37,99,235,0.07)' }}>
            <div className="flex items-center gap-1.5">
              <div className="w-6 h-6 rounded-lg bg-blue-100 flex items-center justify-center">
                <svg className="w-3.5 h-3.5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                </svg>
              </div>
              <span className="text-xs font-black text-slate-800">Facteurs du jour</span>
            </div>

            <div className="flex flex-col gap-1.5">
              <FactorTile icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 10l7-7m0 0l7 7m-7-7v18"/></svg>} label="Densité-alt." value={`${da.densityAltitudeFt} ft`} score={daScore} badgeLabel={daLabel} />
              <FactorTile icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"/></svg>} label="Température" value={`${metar.temperature}°C`} score={tempScore} badgeLabel={tempLabel} />
              <FactorTile icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3c0 0-6 6.5-6 10a6 6 0 0012 0c0-3.5-6-10-6-10z"/></svg>} label="Humidité" value={`${supplemental.humidity}%`} score={humScore} badgeLabel={humLabel} />
              <FactorTile icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z"/></svg>} label="Vent" value={windDesc} score={windScore} badgeLabel={windLabel} />
              <FactorTile icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"/></svg>} label={`Piste ${selectedRunway.label}`} value={`${selectedRunway.lengthFt} ft`} score={0} badgeLabel="Disponible" />
              <FactorTile icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>} label="Visibilité / Plafond" value={`${visText} / ${ceilingText}`} score={visScore} badgeLabel="Info" />
            </div>
          </div>

          {/* ── CARD 3: Piste sélectionnée ── */}
          <div className="rounded-xl border-2 border-green-200 bg-white p-3 flex flex-col gap-2" style={{ boxShadow: '0 2px 8px rgba(22,163,74,0.07)' }}>
            <div className="flex items-center gap-1.5">
              <div className="w-6 h-6 rounded-lg bg-green-100 flex items-center justify-center">
                <svg className="w-3.5 h-3.5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"/>
                </svg>
              </div>
              <span className="text-xs font-black text-slate-800">Piste sélectionnée</span>
            </div>

            {/* Runway selector — flex-wrap for mobile */}
            <div className="flex flex-wrap gap-1">
              <button
                onClick={() => setManualRunway(null)}
                className={`rounded-lg px-2 py-1.5 text-xs font-bold border-2 transition-all ${
                  manualRunway === null ? 'bg-green-100 border-green-400 text-green-700' : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'
                }`}
              >
                Auto
              </button>
              {CYRC_RUNWAY_OPTIONS.map((rwy) => (
                <button
                  key={rwy.id}
                  onClick={() => setManualRunway(rwy.id)}
                  className={`rounded-lg px-2 py-1.5 text-xs font-bold border-2 transition-all ${
                    manualRunway === rwy.id ? 'bg-blue-100 border-blue-400 text-blue-700'
                      : rwy.id === autoRunway.id && manualRunway === null ? 'bg-green-50 border-green-300 text-green-600' :'bg-white border-slate-200 text-slate-500 hover:border-slate-300'
                  }`}
                >
                  {rwy.label}
                </button>
              ))}
            </div>

            {/* Runway info */}
            <div className="rounded-lg bg-slate-50 border border-slate-200 px-2.5 py-2 flex items-center justify-between">
              <div>
                <div className="text-xs font-bold text-slate-500 uppercase leading-none" style={{ fontSize: '0.55rem' }}>PISTE</div>
                <div className="text-2xl font-black text-slate-800 font-tabular leading-tight">{selectedRunway.label}</div>
              </div>
              <div className="text-right">
                <div className="text-xs font-bold text-slate-500 uppercase leading-none" style={{ fontSize: '0.55rem' }}>LONGUEUR DISPONIBLE</div>
                <div className="text-base font-black text-slate-700 font-tabular">{selectedRunway.lengthFt} ft</div>
                <div className="text-xs font-semibold text-slate-400 font-tabular">{selectedRunway.lengthM} m</div>
              </div>
            </div>

            {/* Wind components */}
            <div className="grid grid-cols-3 gap-1">
              <WindComp label="FACE" value={wind.headwindKt} unit="kt" score={0} />
              <WindComp label="ARRIÈRE" value={wind.tailwindKt} unit="kt" score={wind.tailwindKt > 5 ? 3 : wind.tailwindKt > 3 ? 2 : wind.tailwindKt > 0 ? 1 : 0} />
              <WindComp label="TRAV." value={wind.crosswindKt} unit="kt" score={wind.crosswindKt > 15 ? 3 : wind.crosswindKt > 10 ? 2 : 0} />
            </div>

            {/* Auto badge */}
            {manualRunway === null && (
              <div className="rounded-lg bg-green-50 border border-green-200 px-2 py-1 flex items-center gap-1.5">
                <svg className="w-3 h-3 text-green-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"/>
                </svg>
                <span className="text-xs font-semibold text-green-700">Recommandée selon le vent</span>
              </div>
            )}
          </div>
        </div>

        {/* ── CARD 4: Message pilote + sécurité (full width) ── */}
        <div
          className="rounded-xl border-2 p-3 flex flex-col gap-2"
          style={{ borderColor: levelInfo.border, background: levelInfo.bg }}
        >
          <div className="flex items-start gap-2">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5" style={{ background: levelInfo.color + '22' }}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: levelInfo.color }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
              </svg>
            </div>
            <div>
              <div className="text-xs font-black uppercase tracking-wide leading-none mb-1" style={{ color: levelInfo.color, fontSize: '0.6rem' }}>MESSAGE PILOTE</div>
              <p className="text-xs font-semibold text-slate-700 leading-relaxed">{PILOT_MESSAGES[globalLevel]}</p>
            </div>
          </div>

          <div className="rounded-lg border-2 border-amber-300 bg-amber-50 px-2.5 py-2 flex items-start gap-2">
            <svg className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
            </svg>
            <p className="text-xs font-semibold text-amber-800 leading-tight">
              <strong>Indication générique seulement.</strong> Le pilote doit utiliser le POH/manuel de vol de son aéronef, les tableaux de performance, la masse et centrage, les NOTAM, la météo officielle et son jugement.
            </p>
          </div>
        </div>

      </div>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

interface FactorTileProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  score: number;
  badgeLabel: string;
}

function FactorTile({ icon, label, value, score, badgeLabel }: FactorTileProps) {
  const info = LEVELS[indexToLevel(score)];
  return (
    <div className="flex items-center gap-2 rounded-lg bg-slate-50 border border-slate-200 px-2 py-1.5">
      <div className="w-6 h-6 rounded-md flex items-center justify-center shrink-0" style={{ background: info.color + '18', color: info.color }}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-bold text-slate-500 leading-none" style={{ fontSize: '0.6rem' }}>{label}</div>
        <div className="text-sm font-black text-slate-800 font-tabular leading-tight">{value}</div>
      </div>
      <FactorBadge score={score} label={badgeLabel} />
    </div>
  );
}

interface WindCompProps {
  label: string;
  value: number;
  unit: string;
  score: number;
}

function WindComp({ label, value, unit, score }: WindCompProps) {
  const info = LEVELS[indexToLevel(score)];
  const color = score === 0 ? '#16a34a' : info.color;
  const bg = score === 0 ? '#f0fdf4' : info.bg;
  const border = score === 0 ? '#86efac' : info.border;
  return (
    <div className="rounded-lg border px-1.5 py-1.5 text-center flex flex-col items-center justify-center" style={{ background: bg, borderColor: border }}>
      <div className="text-xs font-bold uppercase leading-none" style={{ color, fontSize: '0.55rem' }}>{label}</div>
      <div className="text-base font-black font-tabular leading-tight" style={{ color }}>{value}</div>
      <div className="text-xs font-semibold leading-none" style={{ color, fontSize: '0.55rem' }}>{unit}</div>
    </div>
  );
}
